import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Loader2 } from 'lucide-react';
import { getDefaultLandingPath } from '../lib/permissions';
import type { UserRole } from '../types';

/**
 * AuthCallback component
 * 
 * Handles the Auth0 redirect callback after authentication.
 * Verifies user exists in backend and redirects to dashboard on successful login.
 */
export const AuthCallback: React.FC = () => {
  const { user, isAuthenticated, isLoading, error, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [hasRedirected, setHasRedirected] = useState(false);

  useEffect(() => {
    const handleCallback = async () => {
      if (isLoading) {
        return; // Still loading, wait
      }

      if (hasRedirected) {
        return; // Already redirected, prevent multiple redirects
      }

      if (error) {
        console.error('Auth0 error:', error);
        // On error, redirect back to Auth0 login
        setHasRedirected(true);
        window.location.href = window.location.origin;
        return;
      }

      if (isAuthenticated && user) {
        try {
          // Get access token for API calls
          await getAccessTokenSilently();

          // Verify user exists in backend (this should be handled by Auth0 Actions/Rules)
          // But we can also verify here as a double-check
          if (user.email) {
            // Get returnTo from sessionStorage (set by Auth0Provider's onRedirectCallback)
            const returnTo = sessionStorage.getItem('auth0_returnTo');
            sessionStorage.removeItem('auth0_returnTo'); // Clean up
            
            // Determine dashboard based on user role
            const userRecord = user as Record<string, unknown>;
            const roleClaim = userRecord['https://quotehub.com/roles'] || userRecord.role;
            const role = (
              Array.isArray(roleClaim)
                ? (typeof roleClaim[0] === 'string' ? roleClaim[0] : 'ADVISER')
                : (typeof roleClaim === 'string' ? roleClaim : 'ADVISER')
            ) as UserRole;

            const targetPage = returnTo || getDefaultLandingPath(role);
            
            // Redirect to target page
            setHasRedirected(true);
            navigate(targetPage, { replace: true });
          } else {
            console.error('User email not found');
            // Redirect back to Auth0 login
            setHasRedirected(true);
            window.location.href = window.location.origin;
          }
        } catch (tokenError) {
          console.error('Error getting access token:', tokenError);
          // Redirect back to Auth0 login on error
          setHasRedirected(true);
          window.location.href = window.location.origin;
        }
      } else {
        // Not authenticated, redirect back to Auth0 login
        setHasRedirected(true);
        window.location.href = window.location.origin;
      }
    };

    handleCallback();
  }, [isAuthenticated, isLoading, error, user, getAccessTokenSilently, hasRedirected, navigate]);

  // Show loading state while processing callback
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            Completing sign in...
          </h2>
          <p className="text-slate-600">
            Please wait while we verify your account.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
